import { useMemo } from "react";

import moment from "moment";

import { useUser } from "@components/user-context";
import { formatting, todayLocalAsISODate } from "@north-beam/nb-common";
import { parseDateRangeArgument } from "@utils/index";

import { useUniversalBenchmarksConfiguratorContext } from "./hooks";

export const ShoppingCart = () => {
  const { user } = useUser();
  const {
    baselineFilters: { dateRange },
    daysSelection,
  } = useUniversalBenchmarksConfiguratorContext();

  const dateRangeFormtted = useMemo(() => {
    const interval = parseDateRangeArgument(dateRange, todayLocalAsISODate());
    const startDate = moment(interval.startDate).format("MM/DD/YYYY");
    const endDate = moment(interval.endDate).format("MM/DD/YYYY");

    return `${startDate} – ${endDate}`;
  }, [dateRange]);

  const details = useMemo(() => {
    const days = daysSelection.filter(
      (e) => !(e.isExcluded || e.willBeExcluded),
    );

    const totalDays = days.length;
    let totalOrders = 0;
    let totalRevenue = 0;
    let totalCosts = 0;

    for (const e of days) {
      totalOrders += e.txns;
      totalRevenue += e.revGross;
      totalCosts += e.cogs;
    }

    const totalProfit = totalRevenue - totalCosts;

    return {
      totalDays,
      totalOrders,
      totalRevenue,
      totalCosts,
      totalProfit,
      dailyRevenue: totalRevenue / totalDays,
      dailyCosts: totalCosts / totalDays,
      dailyProfit: totalProfit / totalDays,
      orderRevenue: totalRevenue / totalOrders,
      orderCosts: totalCosts / totalOrders,
      orderProfit: totalProfit / totalOrders,
    };
  }, [daysSelection]);

  const formatCurrency = (value: number) => {
    return formatting.formatCurrency.exact(value, {
      code: user.currencies.currentCurrency,
    });
  };

  return (
    <div className="bg-v2-highlight border border-v2-tertiary rounded p-6 overflow-auto h-full text-v2-primary">
      <h5 className="text-sm">Benchmark Days</h5>
      <div className="space-y-1">
        <Row label="Dates" value={dateRangeFormtted} />
        <Row label="Days included" value={details.totalDays} />
        <Row
          label="Orders included"
          value={formatting.formatInteger.exact(details.totalOrders, {})}
        />
        <Row
          label="Total revenue"
          value={formatCurrency(details.totalRevenue)}
        />
        <Row label="Total costs" value={formatCurrency(details.totalCosts)} />
        <Row label="Total profit" value={formatCurrency(details.totalProfit)} />
      </div>
      <hr className="my-5" />
      <h5 className="text-sm">Average Day</h5>
      <div className="space-y-1">
        <Row
          label="Daily revenue"
          value={formatCurrency(details.dailyRevenue)}
        />
        <Row label="Daily costs" value={formatCurrency(details.dailyCosts)} />
        <Row label="Daily profit" value={formatCurrency(details.dailyProfit)} />
      </div>
      <hr className="my-5" />
      <h5 className="text-sm">Average Order</h5>
      <div className="space-y-1">
        <Row
          label="Order revenue"
          value={formatCurrency(details.orderRevenue)}
        />
        <Row label="Order costs" value={formatCurrency(details.orderCosts)} />
        <Row label="Order profit" value={formatCurrency(details.orderProfit)} />
      </div>
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string | number }) => {
  return (
    <div className="flex items-center justify-between">
      <div className="text-v2-secondary text-xs">{label}</div>
      <div className="font-mono text-xs">{value}</div>
    </div>
  );
};
